import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import Button from '../../../../../../components/buttons/Button';
import type { BingoEventType, MetagameBingoEvent } from '../../../../../../stores/bingo/types';
import { BINGO_WIDGET_TIMER_BUFFER_MS } from '../../../../constants';
import { useBingoWidgetCallbacks, useWidgetAdaptiveText } from '../../../../hooks';
import type { BingoWidgetDataType, BingoWidgetEventMap, BingoWidgetType, UpcomingWidgetEvent } from '../../../../types';
import { BingoWidgetLogo } from '../../BingoWidgetLogo/BingoWidgetLogo';
import { BingoWidgetTimer } from '../../BingoWidgetTimer/BingoWidgetTimer';
import { BingoWideWidgetWrapper } from '../BingoWideWidgetWrapper/BingoWideWidgetWrapper';

import styles from './BingoNewPlayerWideWidget.module.scss';

interface BingoNewPlayerWideWidgetProps {
  data: BingoWidgetDataType;
  widgetType: BingoWidgetType;
}

function getCurrentEvent(
  event: BingoWidgetEventMap[BingoWidgetType],
  widgetType: BingoWidgetType,
): MetagameBingoEvent | null {
  if (widgetType === 'upcoming') {
    const targetEvent = event as UpcomingWidgetEvent;
    if (!targetEvent.upcomingEvents || !targetEvent.upcomingEvents.length) {
      return null;
    }
    return targetEvent.upcomingEvents[0];
  }
  if (widgetType === 'live' || widgetType === 'startingSoon') {
    return event as MetagameBingoEvent;
  }
  return null;
}

const LiveText = ({ bingoType }: { bingoType: BingoEventType }) => {
  const { textRef } = useWidgetAdaptiveText({ padding: 4 });
  const { t } = useTranslation();
  return (
    <span ref={textRef} className={styles.text}>
      {bingoType === 'SWEEP' ? t('prime.bingo.sweepBingo') : t('prime.bingo.ccBingo')}{' '}
      {t('prime.lobbyBanner.isLiveSmall')}
    </span>
  );
};

export function BingoNewPlayerWideWidget(props: BingoNewPlayerWideWidgetProps) {
  const { widgetType, data } = props;
  const { event } = data;

  const { t } = useTranslation();
  const { onPurchaseClick, onEnterLiveEventClick } = useBingoWidgetCallbacks();
  const currentEvent = getCurrentEvent(event, widgetType);

  if (!currentEvent) {
    return null;
  }

  const bingoType = currentEvent.coinType === 'GC' ? 'CASUAL' : 'SWEEP';
  const bufferedStartMs = currentEvent.startDate
    ? new Date(currentEvent.startDate).getTime() + BINGO_WIDGET_TIMER_BUFFER_MS
    : null;
  const timeToStartInMs = bufferedStartMs ? dayjs(bufferedStartMs).diff(dayjs(), 'milliseconds') : 0;

  const isStartingSoon = widgetType === 'startingSoon';
  const timerVisible = widgetType === 'upcoming';
  const isLive = widgetType === 'live';

  return (
    <BingoWideWidgetWrapper
      feature="newPlayer"
      onClick={isLive ? () => onEnterLiveEventClick(currentEvent) : onPurchaseClick}
    >
      <BingoWidgetLogo bingoType={bingoType} sizeType="wide" />
      <div className={styles.content}>
        <div className={styles.topWrapper}>
          {isStartingSoon && <span className={styles.text}>{t('prime.lobbyBanner.startingSoonSm')}</span>}
          {isLive && <LiveText bingoType={bingoType} />}
          {timerVisible && (
            <BingoWidgetTimer
              extended
              newPlayer
              bingoType={bingoType}
              eventId={currentEvent.id}
              startsInMs={timeToStartInMs}
            />
          )}
        </div>
        <div className={styles.btnWrapper}>
          <Button btnType="main" className={styles.btn}>
            {t('prime.lobbyBanner.joinNow')}
          </Button>
        </div>
      </div>
    </BingoWideWidgetWrapper>
  );
}
